import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ChevronDown, ChevronRight, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function DriverPayReport() {
  const [settlements, setSettlements] = useState([]);
  const [loads, setLoads] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Settlement.list('-settlement_date', 500),
      client.entities.Load.list('-delivery_date', 500),
    ]).then(([s, l]) => { setSettlements(s); setLoads(l); setLoading(false); });
  }, []);

  const filtered = settlements.filter(s =>
    inRange(s.settlement_date || s.finalized_date || s.created_date, start, end)
  );

  const byDriver = filtered.reduce((acc, s) => {
    const key = s.driver_email || s.driver_name;
    if (!acc[key]) acc[key] = { name: s.driver_name, email: s.driver_email, records: [] };
    acc[key].records.push(s);
    return acc;
  }, {});

  const driverRows = Object.values(byDriver).map(d => {
    const gross = d.records.reduce((s, r) => s + (r.gross_revenue || 0), 0);
    const net = d.records.reduce((s, r) => s + (r.final_net_pay ?? r.net_driver_pay ?? 0), 0);
    // Was missing lumper/late fees -- Deductions didn't add up to
    // Gross - Net whenever either was charged (Net Pay itself was
    // always correct, since it comes straight from the settlement).
    const deductions = d.records.reduce((s, r) => s + (r.carrier_take_amount || 0) + (r.fuel_advance_total || 0) + (r.dispatch_fee_amount || 0) + (r.lumper_fee_deducted || 0) + (r.late_fee_deducted || 0), 0);
    const miles = d.records.reduce((s, r) => {
      const l = loads.find(lo => lo.id === r.load_id);
      return s + (l?.miles || 0);
    }, 0);
    return { ...d, gross, net, deductions, miles };
  });

  const csvRows = driverRows.map(d => ({
    Driver: d.name,
    'Loads Count': d.records.length,
    'Total Miles': Math.round(d.miles),
    'Gross Revenue': d.gross.toFixed(2),
    Deductions: d.deductions.toFixed(2),
    'Net Pay': d.net.toFixed(2),
  }));

  const handlePrint = () => {
    const cols = ['Driver', 'Loads', 'Total Miles', 'Gross Revenue', 'Deductions', 'Net Pay'];
    const dataRows = driverRows.map(d => [
      { value: d.name, class: 'bold' }, d.records.length, Math.round(d.miles).toLocaleString(),
      { value: `$${d.gross.toLocaleString()}`, class: 'positive' },
      { value: `-$${d.deductions.toFixed(2)}`, class: 'negative' },
      { value: `$${d.net.toFixed(2)}`, class: 'positive' },
    ]);
    const grandGross = driverRows.reduce((s, d) => s + d.gross, 0);
    const grandNet = driverRows.reduce((s, d) => s + d.net, 0);
    const grandLoads = driverRows.reduce((s, d) => s + d.records.length, 0);
    const totalsRow = [
      'TOTALS', grandLoads, '',
      { value: `$${grandGross.toLocaleString()}`, class: 'gold' }, '',
      { value: `$${grandNet.toFixed(2)}`, class: 'gold' },
    ];
    const cards = [
      { label: 'Drivers', value: driverRows.length, color: 'gold' },
      { label: 'Total Loads', value: grandLoads, color: '' },
      { label: 'Gross Revenue', value: `$${grandGross.toLocaleString()}`, color: 'positive' },
      { label: 'Total Net Pay', value: `$${grandNet.toFixed(2)}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Driver Pay Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'driver-pay-report.csv')} exportDisabled={loading || driverRows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['', 'Driver', 'Loads', 'Total Miles', 'Gross Revenue', 'Deductions', 'Net Pay'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {driverRows.map(d => (
              <>
                <tr key={d.email} className="hover:bg-white/3 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [d.email]: !p[d.email] }))}>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {expanded[d.email] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </td>
                  <td className="px-3 py-2.5 font-semibold text-foreground">{d.name}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{d.records.length}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{Math.round(d.miles).toLocaleString()}</td>
                  <td className="px-3 py-2.5 font-semibold text-foreground">${d.gross.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-red-400">−${d.deductions.toFixed(2)}</td>
                  <td className="px-3 py-2.5 font-bold text-green-400">${d.net.toFixed(2)}</td>
                </tr>
                {expanded[d.email] && d.records.map(r => {
                  const l = loads.find(lo => lo.id === r.load_id);
                  return (
                    <tr key={r.id} className="bg-white/2">
                      <td></td>
                      <td className="px-3 py-2 pl-8 text-muted-foreground" colSpan={1}>└ #{r.load_number}</td>
                      <td className="px-3 py-2 text-muted-foreground">{l?.pickup_date || '—'} → {l?.delivery_date || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{l?.miles || '—'}</td>
                      <td className="px-3 py-2 text-foreground">${(r.gross_revenue || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-red-400">−${((r.carrier_take_amount || 0) + (r.fuel_advance_total || 0) + (r.dispatch_fee_amount || 0) + (r.lumper_fee_deducted || 0) + (r.late_fee_deducted || 0)).toFixed(2)}</td>
                      <td className="px-3 py-2 text-green-400">${(r.final_net_pay ?? r.net_driver_pay ?? 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </>
            ))}
          </tbody>
        </table>
        {driverRows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No data for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}